import React from 'react';
import { useNavigate } from 'react-router-dom';
import heliosLogo from '../assets/helios-logo-real.png';

/**
 * Home Component
 * 
 * Public-facing home page for the HelpDesk Agent.
 * Displays features and provides navigation to chat interface.
 * Admin portal is NOT exposed on this page.
 * 
 * Returns:
 *     JSX: Home page with features and navigation
 */
function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-dark-900 text-white flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Animated background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/5 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-background-float"></div>
        <div className="absolute top-1/4 right-1/5 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-background-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-background-float" style={{animationDelay: '4s'}}></div>
      </div>

      {/* Content */}
      <div className="max-w-4xl w-full text-center animate-fade-in-up relative z-10">
        {/* Logo */}
        <div className="flex items-center justify-center mb-12">
          <img
            src={heliosLogo}
            alt="Helios Core"
            className="h-32 w-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300 animate-logo-float"
          />
        </div>

        {/* Title Section */}
        <div className="mb-12">
          <h2 className="text-5xl md:text-6xl font-light mb-4 gradient-text">
            AI Helpdesk Agent
          </h2>
          <p className="text-xl text-dark-300 max-w-2xl mx-auto leading-relaxed">
            Secure IT support powered by Microsoft Graph and intelligent automation
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-3xl mx-auto">
          <div className="card">
            <div className="text-4xl mb-4">🔐</div>
            <h3 className="text-lg font-semibold mb-2">Secure Identity Verification</h3>
            <p className="text-dark-400">Multi-factor authentication with SMS verification</p>
          </div>
          <div className="card">
            <div className="text-4xl mb-4">🔄</div>
            <h3 className="text-lg font-semibold mb-2">Password Reset</h3>
            <p className="text-dark-400">Instant password reset through Microsoft Graph</p>
          </div>
          <div className="card">
            <div className="text-4xl mb-4">🔓</div>
            <h3 className="text-lg font-semibold mb-2">Account Unlock</h3>
            <p className="text-dark-400">Automated account unlock for locked users</p>
          </div>
        </div>

        {/* Action Buttons - Only Chat, No Admin */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            className="btn-primary"
            onClick={() => navigate('/chat')}
          >
            Get Help Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;

